/* eslint-disable no-await-in-loop */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getFileExtension } from '@utils/common';
import { handleErrorNotification } from '@utils/error';
import IntervalManager from '@utils/intervalManager';
import {
  getODataConjunctionFilterFrom,
  getODataFilterFrom,
  getODataRangeFilterFrom,
  normalizeODataStringsField,
} from '@utils/odata';
import { getFilePreviewBlobByTypeApi as getFilePreviewBlobByTypeExternalApi } from '@api/external/data';
import { UploadFileType } from '@constants/constant';
import {
  DELETE_FILE_SUCCESS_MESSAGE,
  FILE_META_UPDATED_MESSAGE,
  FILE_RETRY_INDEXING_BY_STATUS_SUCCESS_MESSAGE,
  FILE_RETRY_INDEXING_SUCCESS_MESSAGE,
  FILE_SORTING_PARAMS_BY_KEY,
} from '@constants/data-page';
import { SelectedFilters } from '@constants/data-table';
import { PollingKey } from '@redux/reducers/fileReducer';
import { initialState } from '@redux/rootReducer';
import { RootState } from '@redux/store';
import {
  DELETE_ALL_FILES,
  DELETE_FILE,
  DELETE_MULTIPLE_FILES,
  DOWNLOAD_FILE,
  GET_FILE_CONTENT,
  GET_INDEX_DOCUMENTS_PER_FILE,
  GET_INDEX_FILES,
  GET_SAMPLE_DATASET_LIST,
  GET_WORKSPACE_FILES,
  IDocumentsPerFile,
  IMPORT_SAMPLE_DATASET,
  ISelectedFileDocument,
  MetadataFiltersValue,
  NotificationDuration,
  NotificationType,
  POLLING_INDEX_FILES,
  RESET_FILE_CONTENT,
  RESET_FILES_MESSAGE,
  RETRY_INDEX_BY_STATUS,
  RETRY_INDEX_FILE,
  SELECT_FILE_DOCUMENT,
  SELECT_FILE_SORT_VALUE,
  SET_POLLING_INTERVAL,
  STOP_POLLING,
  UPDATE_FILE_META,
} from '@redux/types/types';
import { IndexFileStatus } from '@modules/Indexes/constants/indexes';
import {
  deleteAllWorkspaceFilesApi,
  deleteWorkspaceFilesApi,
  getFileBlobApi,
  getFilePreviewBlobByTypeApi,
  getIndexDocumentsPerFileApi,
  getIndexFilesApi,
  getSampleDatasetsApi,
  getWorkspaceFilesApi,
  IAllFilesData,
  importSampleDatasetApi,
  retryIndexByStatusApi,
  retryIndexFileApi,
  updateFileMetaApi,
} from '@src/api/data';
import { downloadBlob } from '@src/utils/file';
import { addNotification, addSequentialNotification } from './notificationActions';

export const resetFilesMessage = {
  type: RESET_FILES_MESSAGE,
};

export const resetFileContent = {
  type: RESET_FILE_CONTENT,
};

export const selectSortValue = (value: string) => ({
  type: SELECT_FILE_SORT_VALUE,
  payload: value,
});

export const selectFileDocument = (payload: ISelectedFileDocument | null) => ({
  type: SELECT_FILE_DOCUMENT,
  payload,
});

export const setPollingInterval = (payload: { key: PollingKey; interval: number }) => ({
  type: SET_POLLING_INTERVAL,
  payload,
});

const getMetadataFilteringLogic = (metadataFilterValues: Record<string, MetadataFiltersValue>) => {
  return Object.keys(metadataFilterValues).reduce((parsedFilters, filterKey) => {
    const isFirstFilter = !parsedFilters;
    let parsedFilter = '';

    if (
      (metadataFilterValues[filterKey] as any).min ||
      (metadataFilterValues[filterKey] as any).max
    ) {
      parsedFilter = getODataRangeFilterFrom(filterKey, [
        (metadataFilterValues[filterKey] as any).min,
        (metadataFilterValues[filterKey] as any).max,
      ]);
    } else {
      const filter = metadataFilterValues[filterKey] as unknown[];
      filter.forEach((filterValue, index) => {
        if (index === 0 && filter.length > 1 && Object.keys(metadataFilterValues).length > 1)
          parsedFilter = parsedFilter.concat(`(`);
        // FIXME: Not using `getODataEQFilterFrom` func as the files endpoint throws an error when
        // an UUID is passed without '' on a OData filter
        parsedFilter = parsedFilter.concat(
          `${normalizeODataStringsField(filterKey)} eq '${filterValue}'`,
        );
        if (index < Object.keys(filter).length - 1) parsedFilter = parsedFilter.concat(` or `);
        else if (filter.length > 1 && Object.keys(metadataFilterValues).length > 1)
          parsedFilter = parsedFilter.concat(`)`);
      }, '');
    }

    if (isFirstFilter) return parsedFilter;

    return `${parsedFilters} and ${parsedFilter}`;
  }, '');
};

export const getWorkspaceFiles = createAsyncThunk(
  GET_WORKSPACE_FILES,
  async (
    {
      currentPage,
      pageSize,
      searchValue,
      sortValue,
      after,
      metadataFilterValues = {},
    }: {
      currentPage: number;
      pageSize: number;
      searchValue: string;
      sortValue?: string;
      after?: {
        value: string;
        fileId: string;
      };
      metadataFilterValues?: Record<string, MetadataFiltersValue>;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const { sortValue: defaultSortValue } = (getState() as typeof initialState).fileStore;
    const sortingKey = (sortValue || defaultSortValue) as keyof typeof FILE_SORTING_PARAMS_BY_KEY;
    dispatch(selectSortValue(sortingKey));
    const { field, order } = FILE_SORTING_PARAMS_BY_KEY[sortingKey] || {};

    const metadataFilteringLogic = getMetadataFilteringLogic(metadataFilterValues);

    const allFilesData: IAllFilesData = {
      page_number: currentPage,
      limit: pageSize,
      name: searchValue,
      after,
      field,
      order,
      filters: metadataFilteringLogic,
    };
    try {
      const response = await getWorkspaceFilesApi(allFilesData);
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const getIndexFiles = createAsyncThunk(
  GET_INDEX_FILES,
  async (
    {
      indexName,
      currentPage,
      pageSize,
      searchValue,
      sortValue,
      filterValues,
      after,
      metadataFilterValues = {},
    }: {
      indexName: string;
      currentPage: number;
      pageSize: number;
      searchValue: string;
      sortValue?: string;
      filterValues?: SelectedFilters;
      after?: { value: string; fileId: string };
      metadataFilterValues?: Record<string, MetadataFiltersValue>;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const { sortValue: defaultSortValue } = (getState() as typeof initialState).fileStore;
    const sortingKey = (sortValue || defaultSortValue) as keyof typeof FILE_SORTING_PARAMS_BY_KEY;
    dispatch(selectSortValue(sortingKey));
    const { field, order } = FILE_SORTING_PARAMS_BY_KEY[sortingKey] || {};

    const metadataFilteringLogic = getMetadataFilteringLogic(metadataFilterValues);
    const odataFilter = filterValues && getODataFilterFrom(filterValues);
    const odataConjunctionFilter = getODataConjunctionFilterFrom(
      metadataFilteringLogic,
      odataFilter,
    );

    const allFilesData: IAllFilesData = {
      page_number: currentPage,
      limit: pageSize,
      name: searchValue,
      after,
      field,
      order,
      filters: odataConjunctionFilter,
    };

    try {
      const response = await getIndexFilesApi(indexName, allFilesData);
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const stopPollingIndexFiles = () => {
  return {
    type: STOP_POLLING,
    payload: POLLING_INDEX_FILES,
  };
};

export const startPollingIndexFiles = createAsyncThunk(
  POLLING_INDEX_FILES,
  async (
    {
      indexName,
      currentPage,
      pageSize,
      searchValue,
      sortValue,
      filterValues,
      after,
      metadataFilterValues = {},
      interval,
    }: {
      indexName: string;
      currentPage: number;
      pageSize: number;
      searchValue: string;
      sortValue?: string;
      filterValues?: SelectedFilters;
      after?: { value: string; fileId: string };
      metadataFilterValues?: Record<string, MetadataFiltersValue>;
      interval?: number;
    },
    { dispatch, getState },
  ) => {
    const { pollingIntervals } = (getState() as RootState).fileStore;
    const pollingKey = POLLING_INDEX_FILES;
    const intervalManager = IntervalManager.getInstance();

    // Stop any existing polling first
    dispatch(stopPollingIndexFiles());
    intervalManager.clear(pollingKey);

    if (interval) {
      dispatch(setPollingInterval({ key: pollingKey, interval }));
    }

    const effectiveInterval = interval || pollingIntervals[pollingKey];

    intervalManager.create(
      pollingKey,
      () => {
        dispatch(
          getIndexFiles({
            indexName,
            currentPage,
            pageSize,
            searchValue,
            sortValue,
            filterValues,
            after,
            metadataFilterValues,
          }),
        );
      },
      effectiveInterval,
    );
  },
);

export const getIndexDocumentsPerFile = createAsyncThunk(
  GET_INDEX_DOCUMENTS_PER_FILE,
  async (
    { indexName, fileId }: { indexName: string; fileId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await getIndexDocumentsPerFileApi(indexName, fileId);
      let documents: Record<string, IDocumentsPerFile>[] = [];

      // Handle both string and array responses
      const data =
        typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      data.split('\n').forEach((line: string) => {
        try {
          const document = JSON.parse(line);
          documents.push(document);
        } catch {}
      });
      return documents;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const retryIndexFile = createAsyncThunk(
  RETRY_INDEX_FILE,
  async (
    { indexName, fileId }: { indexName: string; fileId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await retryIndexFileApi(indexName, fileId);
      dispatch(
        addNotification({
          content: FILE_RETRY_INDEXING_SUCCESS_MESSAGE,
          type: NotificationType.Success,
          duration: NotificationDuration.long,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const retryIndexByStatus = createAsyncThunk(
  RETRY_INDEX_BY_STATUS,
  async (
    params: { indexName: string; fileStatus: IndexFileStatus },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await retryIndexByStatusApi(params.indexName, params.fileStatus);
      dispatch(
        addNotification({
          content: FILE_RETRY_INDEXING_BY_STATUS_SUCCESS_MESSAGE,
          type: NotificationType.Success,
          duration: NotificationDuration.long,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const downloadFile = createAsyncThunk(
  DOWNLOAD_FILE,
  async (
    { fileId, fileName }: { fileId: string; fileName: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await getFileBlobApi(fileId);
      downloadBlob(fileName, response.data);
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const getFileContent = createAsyncThunk(
  GET_FILE_CONTENT,
  async (
    {
      fileId,
      fileName,
      isExternal = false,
    }: { fileId: string; fileName?: string; isExternal?: boolean },
    { dispatch, rejectWithValue, getState },
  ) => {
    const apiCall = isExternal ? getFilePreviewBlobByTypeExternalApi : getFilePreviewBlobByTypeApi;
    let workspace: string | undefined;
    const fileType = fileName ? (getFileExtension(fileName) as UploadFileType) : null;

    if (isExternal) {
      const { tokenData } = (getState() as typeof initialState).sharedPrototypeStore;
      workspace = tokenData?.workspaceName;
    }

    try {
      const response = await apiCall(fileId, fileType, workspace);
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const deleteFile = createAsyncThunk(
  DELETE_FILE,
  async (fileId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteWorkspaceFilesApi(fileId);
      dispatch(
        addNotification({
          content: DELETE_FILE_SUCCESS_MESSAGE.content,
          title: DELETE_FILE_SUCCESS_MESSAGE.title,
          type: NotificationType.Success,
          duration: NotificationDuration.long,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const deleteMultipleFiles = createAsyncThunk(
  DELETE_MULTIPLE_FILES,
  async (fileIDs: string[], { dispatch, rejectWithValue }) => {
    try {
      let position = 0;
      const batchSize = 20;

      while (position < fileIDs.length) {
        const batch = fileIDs.slice(position, position + batchSize);
        await Promise.all(batch.map((fileId: string) => deleteWorkspaceFilesApi(fileId)));
        position += batchSize;
      }

      dispatch(
        addNotification({
          content: DELETE_FILE_SUCCESS_MESSAGE.content,
          title: DELETE_FILE_SUCCESS_MESSAGE.title,
          type: NotificationType.Success,
          duration: NotificationDuration.long,
        }),
      );

      return true;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const deleteAllFiles = createAsyncThunk(
  DELETE_ALL_FILES,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await deleteAllWorkspaceFilesApi();

      dispatch(
        addNotification({
          content: DELETE_FILE_SUCCESS_MESSAGE.content,
          title: DELETE_FILE_SUCCESS_MESSAGE.title,
          type: NotificationType.Success,
          duration: NotificationDuration.long,
        }),
      );

      return true;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const updateFileMeta = createAsyncThunk(
  UPDATE_FILE_META,
  async (
    { fileId, meta }: { fileId: string; meta: Record<string, unknown> },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await updateFileMetaApi(fileId, meta);
      dispatch(
        addNotification({
          content: FILE_META_UPDATED_MESSAGE,
          type: NotificationType.Success,
        }),
      );
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const getSampleDatasets = createAsyncThunk(
  GET_SAMPLE_DATASET_LIST,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSampleDatasetsApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const importSampleDataset = createAsyncThunk(
  IMPORT_SAMPLE_DATASET,
  async (dataset: string, { dispatch, rejectWithValue }) => {
    try {
      dispatch(
        addSequentialNotification({
          content: 'Importing sample files to workspace.',
          type: NotificationType.Loading,
          duration: 0,
        }),
      );

      const response = await importSampleDatasetApi(dataset);

      dispatch(
        addSequentialNotification({
          content: 'Sample files imported successfully.',
          type: NotificationType.Success,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch, {
        sequential: true,
      });
      return rejectWithValue(error);
    }
  },
);
